import { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useContext, useState } from "react";

import { DataContext } from "@/provider/DataContextProvider";
import { checkCscLogin } from "@/services/api";
import { ensurePublicTokenIsFresh } from "@/services/ensurePublicTokenIsFresh";
import { BasicCustomerDataTwoFactorMethod } from "@/services/model";
import { CUSTOMER_AUTHORIZATION, TOKEN_URL } from "@/utils/apiUrls";
import { setAuthAtom, showsSessionExpiredModalAtom } from "@/utils/atoms";

/**
 * This hook provides authentication functionality for logging in customers using username/password credentials. It handles the authentication process, token management, and state updates
 *
 * This serves to remove the older login flow (see Ticket 839)
 */
interface Props {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}

export const useCustomerLogin = (options?: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const { setDataContext } = useContext(DataContext);

  const [twoFactorGrantToken, setTwoFactorGrantToken] = useState();
  const [shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal] =
    useState(false);
  const [shouldShowTwoFactorModal, setShouldShowTwoFactorModal] =
    useState(false);
  const [twoFactorMethod, setTwoFactorMethod] =
    useState<BasicCustomerDataTwoFactorMethod>(
      BasicCustomerDataTwoFactorMethod.STANDARD,
    );

  const loginAsCustomer = async ({
    email,
    password,
    skipEmailVerificationCheck,
    isRetry = false,
  }: {
    email: string;
    password: string;
    skipEmailVerificationCheck?: boolean;
    isRetry?: boolean;
  }) => {
    setIsLoading(true);
    setError(null);

    checkCscLogin(email)
      .then(async (response) => {
        setDataContext((prev) => ({
          ...prev,
          isEmailVerified: !!response.emailVerified,
        }));

        // Explicitly check for 'emailVerified === false' as if email adress is not yet taken it will not be sent by backend - so would be undefined
        if (!skipEmailVerificationCheck && response.emailVerified === false) {
          setShouldShowVerifyEmailModal(true);
          setIsLoading(false);

          return; // STOP normal login here
        }

        try {
          const formData = new URLSearchParams();
          formData.append("grant_type", "password");
          formData.append("username", email);
          formData.append("password", password);

          const tokenResponse = await fetch(TOKEN_URL.toString(), {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: CUSTOMER_AUTHORIZATION,
            },
            body: formData,
          });

          if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json().catch(() => ({}));

            const error = new AxiosError(
              errorData.message ||
                errorData.error_description ||
                "Login failed",
              errorData.error_code || undefined,
              {
                method: "POST",
                url: tokenResponse.url,
                headers: Object.fromEntries(tokenResponse.headers) as any,
              },
              null,
              {
                // Response
                data: errorData,
                status: tokenResponse.status,
                statusText: tokenResponse.statusText,
                headers: {},
                config: {} as any,
              },
            );

            setError(error);
            throw error;
          }

          const tokenData = await tokenResponse.json();

          // Response returns twoFactorGrantToken means user has 2fa and we want to set the (temp) token
          if (tokenData.twoFactorGrantToken) {
            setTwoFactorGrantToken(tokenData.twoFactorGrantToken);
          } else if (!tokenData.access_token || !tokenData.refresh_token) {
            throw new Error("Invalid token response");
          }
          // If neither twoFactorGrantToken nor Error applied it means we need to set user as logged in
          else {
            setAuth({
              accessToken: tokenData.access_token,
              refreshToken: tokenData.refresh_token,
              isAnonymous: false,
            });

            // Clear session expired modal state on successful login
            setShowSessionExpiredModal(undefined);

            if (options?.onSuccess) {
              options.onSuccess();
            }
          }

          if (
            response.method &&
            response.method !== BasicCustomerDataTwoFactorMethod.STANDARD
          ) {
            setTwoFactorMethod(response.method);
            setShouldShowTwoFactorModal(true);
          }

          return tokenData;
        } catch (error) {
          setError(error as AxiosError);

          if (options?.onError) {
            options.onError(error as AxiosError);
          }

          // TODO: refactor when backend sends unified error codes
          if ((error as Error).message === "Der code wurde bereits versendet") {
            // If 2fa SMS Code was already sent we open 2fa modal again to enable user to login
            setShouldShowTwoFactorModal(true);
          } else {
            // If login failed 2fa modal should not be shown
            setShouldShowTwoFactorModal(false);

            throw new Error("Login failed");
          }
        } finally {
          setIsLoading(false);
        }
      })
      .catch((error) => {
        const responseStatus = error?.response?.status;

        if (responseStatus === 401 && !isRetry) {
          ensurePublicTokenIsFresh()
            .then(() =>
              loginAsCustomer({
                email,
                password,
                skipEmailVerificationCheck,
                isRetry: true,
              }),
            )
            .catch((refreshError) => {
              setIsLoading(false);
              // If token refresh failed, call error handler
              if (options?.onError) {
                options.onError(refreshError as AxiosError);
              }
            });
          // Return early to prevent setting loading to false here
          // Loading will be managed by the retry or the catch above

          return;
        } else {
          // Only call error handler if this is an error from checkCscLogin
          // Errors from token fetch are already handled in the inner catch
          if (options?.onError && (error as Error).message !== "Login failed") {
            options.onError(error as AxiosError);
          }
        }

        // If login failed 2fa modal should not be shown
        setShouldShowTwoFactorModal(false);
        setIsLoading(false);
      });
  };

  return {
    loginAsCustomer,
    isLoading,
    setIsLoading,
    error,
    shouldShowTwoFactorModal,
    setShouldShowTwoFactorModal,
    twoFactorGrantToken,
    twoFactorMethod,
    shouldShowVerifyEmailModal,
    setShouldShowVerifyEmailModal,
  };
};
